import React, { useEffect, useState, lazy } from 'react'
import PropTypes from 'prop-types'
import constants from '../../../common/constants'
import BreadcrumbsComponent from '../../../components/Breadcrumbs'
import getOkToShopInfo from '../../../common/clients/getOkToShopInfo'

import ProductDetailCard from '../../../functional-components/ProductDetailCard'

import useWindowSize from '../../../hooks/useWindowSize'
import { useIsTenant } from '../../../contexts/TenantContext'
import ClientAPI from '../../../common/ClientAPI'
import './ProductDetailContent.css'

import { getTenant } from '../../../Helpers'
import texts from '../../../common/texts.json'
import { useClient } from '../../../hooks/useClient'

import { ORIGINS } from '../../../common/GoogleTagManager'
import { useAnalytics } from '../../../contexts/AnalyticsContext'
import AnalyticsConstants from '../../../contexts/AnalyticsContext/constants.json'
import { getUserWalmartIdAndEmail } from '../../../common/ProfileUtils'
import urlToggleHelper from '../../../Helpers/urlToggleHelper'

const ProductRecommendations = lazy(() => import('../../../components/ProductDetailPage/ProductRecommendations'))
import ProductRecommendationsSkeleton from '../../../components/ProductDetailPage/ProductRecommendations/Skeleton'
import RenderOnViewportEntry from '../../../functional-components/RenderOnViewportEntry'
import getConfig from '../../../Helpers/getConfig'
import BannerFreeShippingSod from '../../../components/BannerFreeShippingSod'
const Specifications = lazy(() => import('./Specifications'))

const ProductDetailContent = ({
  productData,
  isWalstore,
  categoriesBreadcrum,
  flowType,
  dtSearchEngine,
  selectedSODStoreId,
  complementariesConfig,
  updateProduct,
  plpPosition,
  searchState,
}) => {
  const { isMobile } = useWindowSize()
  const isSod = useIsTenant(constants.tenant.SOD)
  const [okToShopInfo, setOkToShopInfo] = useState(undefined)
  const [bannersPDP, setBannersPDP] = useState({})
  const [bannersPDPFreeShipping, setBannersPDPFreeShipping] = useState({})
  const { getDataForGoogleAnalytics } = useAnalytics()
  const { isOriginIntegrationEnable } = urlToggleHelper()
  const clientAPI = new ClientAPI()
  const {
    enabled: isComplementariesEnabled,
    enabledV2: isComplementariesEnabledV2,
    maxRecommendations: complementariesMaxRecommendations,
    validateStock: validateComplementariesStock,
  } = complementariesConfig

  const shouldShowNutritionalInfo = productData?.nutritionalInformation
  const hasOnlySpecifications = !(okToShopInfo || productData.nutritionalInformation)

  const isMKP = productData?.isMKP ?? false
  const shouldShowMkpComplementaries = isComplementariesEnabledV2 || !isMKP
  const showComplementaries = isComplementariesEnabled && shouldShowMkpComplementaries && !isWalstore
  const { EVENTS, FLOW_TYPES, INFO_LIST_PAGE } = AnalyticsConstants

  const getStoreId = (tenant) => {
    const {
      tenant: { SOD, CATEX },
    } = constants
    const dictionary = {
      [SOD]:
        selectedSODStoreId && selectedSODStoreId !== 0 ? [selectedSODStoreId] : [constants.stores.DEFAULT_STORE_ID],
      [CATEX]: constants.stores.DEFAULT_IMS_STORE_ID_CATEX,
    }
    return dictionary[tenant]
  }

  const complementariesCall = () =>
    clientAPI.getComplementaryProducts({
      sku: productData.sku,
      storeIds: getStoreId(getTenant()),
      validateStock: validateComplementariesStock,
      maxRecommendations: complementariesMaxRecommendations,
      walmartId: isOriginIntegrationEnable ? undefined : getUserWalmartIdAndEmail().walmartId,
    })

  const [
    {
      data: productComplementariesResponse,
      loading: loadingProductComplementaries,
      error: errorOnProductComplementaries,
    },
    fetchComplementaries,
  ] = useClient()

  useEffect(() => {
    if (showComplementaries) {
      fetchComplementaries(complementariesCall)
    }

    const fetchData = async () => {
      const { bannersPDP, freeShipping = {} } = await clientAPI.getAds(['bannersPDP'])
      setBannersPDP(bannersPDP)
      setBannersPDPFreeShipping(freeShipping)
    }
    fetchData()
  }, [])

  useEffect(() => {
    const loadOkToShop = async () => {
      try {
        const _okToShopInfo = await getOkToShopInfo(productData.gtin13)
        setOkToShopInfo(_okToShopInfo)
      } catch (error) {
        setOkToShopInfo(null)
      }
    }

    if (isSod) loadOkToShop()
    getDataForGoogleAnalytics({
      eventName: EVENTS.VIEW_ITEM,
      flowType,
      data: { ...productData, origin: ORIGINS.PDP, selectedSODStoreId, dtSearchEngine },
    })
  }, [])

  useEffect(() => {
    if (productComplementariesResponse?.data) {
      getDataForGoogleAnalytics({
        eventName: EVENTS.ITEM_LIST_VIEW,
        flowType: FLOW_TYPES.RECOMMENDER,
        data: {
          page: INFO_LIST_PAGE.DEFAULT_PAGE,
          nbHits: INFO_LIST_PAGE.DEFAULT_NBHITS,
          nbPages: INFO_LIST_PAGE.DEFAULT_NGPAGES,
          products: productComplementariesResponse.data,
          origin: ORIGINS.PDP,
          tracking_id: productComplementariesResponse.headers[constants.HEADERS.RECOMMENDER_ANALYTICS_ID],
        },
      })
    }
  }, [productComplementariesResponse])

  const breadcrumbsMargin = () => (isSod ? '0 0 16px 0' : '15px 0 16px 0')

  return (
    <>
      <div className="m-auto pb-20 product-detail__card-section" data-testid="product-detail-content-testid">
        <BannerFreeShippingSod
          isMobile={isMobile}
          freeShipping={bannersPDPFreeShipping}
          configData={getConfig()}
          className={'banner-freeshipping-pdp'}
          origin={ORIGINS.PDP}
        />
        <BreadcrumbsComponent margin={breadcrumbsMargin()} {...categoriesBreadcrum} />
        <ProductDetailCard
          bannersPDP={bannersPDP}
          origin={ORIGINS.PDP}
          hit={productData}
          isWalstore={isWalstore}
          isSod={isSod}
          flowType={flowType}
          updateProduct={updateProduct}
          searchState={searchState}
          plpPosition={plpPosition}
        />
      </div>

      {showComplementaries && (
        <RenderOnViewportEntry
          fallback={<ProductRecommendationsSkeleton className="product-detail__card-section m-auto pb-3" />}
        >
          <ProductRecommendations
            wrapperClasses="m-auto product-detail__card-section pt-3"
            title={`${texts.PRODUCT_RECOMMENDATIONS[getTenant().toUpperCase()]}`}
            origin={ORIGINS.PDP}
            isMobile={isMobile}
            isLoading={loadingProductComplementaries}
            error={errorOnProductComplementaries}
            products={productComplementariesResponse?.data}
            flowType={FLOW_TYPES.RECOMMENDER}
            tenant={getTenant()}
          />
        </RenderOnViewportEntry>
      )}
      {(!isSod || hasOnlySpecifications || okToShopInfo || shouldShowNutritionalInfo) && (
        <RenderOnViewportEntry fallback={<div />}>
          <Specifications productData={productData} isMobile={isMobile} isSod={isSod} okToShopInfo={okToShopInfo} />
        </RenderOnViewportEntry>
      )}
    </>
  )
}

ProductDetailContent.defaultProps = {
  bannersPDP: {},
  isWalstore: false,
  selectedSODStoreId: 0,
  complementariesConfig: {
    enabled: false,
    validateStock: false,
    maxRecommendations: 0,
  },
}

ProductDetailContent.propTypes = {
  bannersPDP: PropTypes.object,
  productData: PropTypes.object.isRequired,
  isWalstore: PropTypes.bool,
  categoriesBreadcrum: PropTypes.object.isRequired,
  complementariesConfig: PropTypes.object,
  flowType: PropTypes.string.isRequired,
  selectedSODStoreId: PropTypes.number,
  searchState: PropTypes.object,
  plpPosition: PropTypes.number,
}

export default ProductDetailContent
